import { useGeolocated } from 'react-geolocated';

import { useGooglePlaces } from '@jane/bloom/hooks/useGooglePlaces';
import type { StoreSwitcherShoppingPreferences } from '@jane/bloom/types/schemas/bloom/storeSwitcher';
import { logError } from '@jane/bloom/utils/logError';

interface CustomGeolocatedArgs {
  handleDetectedAddress: (nextAddress: string) => void;
  setBusy: (param: boolean) => void;
  setLocationButtonDisabled: (param: boolean) => void;
  updateShoppingPreferences: (
    updates: Partial<StoreSwitcherShoppingPreferences>
  ) => void;
}

/**
 * Provides a handler that, when called, converts the shopper's rough coordinates into a
 * street address that is reusable as shopping preferences.
 */
export const useCustomGeolocated = ({
  handleDetectedAddress,
  setBusy,
  setLocationButtonDisabled,
  updateShoppingPreferences,
}: CustomGeolocatedArgs) => {
  const { reverseGeocode } = useGooglePlaces();

  const { getPosition } = useGeolocated({
    async onError(error) {
      const permissions = await navigator?.permissions?.query({
        name: 'geolocation',
      });

      /** Safari privacy rules do not allow us to distinguish between permission states on error */
      const isSafariDenyExplicit =
        navigator.userAgent.includes('Safari') &&
        !navigator.userAgent.includes('Chrome') &&
        !navigator.userAgent.includes('Chromium') &&
        error &&
        error.code === error.PERMISSION_DENIED;

      if (permissions.state === 'denied' || isSafariDenyExplicit) {
        setLocationButtonDisabled(true);
      }

      if (
        error &&
        (error.code === error.POSITION_UNAVAILABLE ||
          error.code === error.TIMEOUT)
      ) {
        logError(error);
      }
    },
    async onSuccess(position) {
      setBusy(true);

      const lat = position.coords.latitude;
      const long = position.coords.longitude;

      updateShoppingPreferences({ latitude: lat, longitude: long });

      try {
        const response = await reverseGeocode(lat, long);

        if (response) {
          handleDetectedAddress(response.formattedAddress);
          updateShoppingPreferences({
            city: response.city,
            countryCode: response.countryCode,
            state: response.state,
            streetAddress: response.street,
            streetAddress2: response.street2,
            userEnteredAddress: response.formattedAddress,
            zipcode: response.zipcode,
          });
        } else {
          handleDetectedAddress('Current location');
        }
      } catch (error) {
        handleDetectedAddress('Current location');
        logError(error);
      }

      setBusy(false);
    },
    suppressLocationOnMount: true,
    watchLocationPermissionChange: true,
  });

  return {
    triggerLocationPrompt: getPosition,
  };
};
